/**
 * Vehicle Detail Page (VDP)
 * ===================================
 * Loads inventory, finds the vehicle keyed by the `?stock=` query param, and
 * renders the image gallery, spec table, features, quick specs, and a
 * "similar vehicles" section. Bails to the not-found/error state when the
 * stock param is missing or does not match a vehicle.
 *
 * Ported from the inline IIFE previously in vehicle-detail.html.
 */

import { loadInventory } from '../util/inventory-loader.ts';
import { buildVehicleCard, initPrequalTriggers, carPlaceholderHtml } from '../util/vehicle-card.ts';
import { conditionLabel, formatPrice } from '../util/format-utils.ts';
import { loadIntegrationScript } from '../util/site-config.ts';
import type { StaticInventoryItem } from '@flexpath/core/src/types/staticInventory-types.d.ts';

let images: string[] = [];
let currentIdx = 0;

/* ── Gallery ── */
function showImage(idx: number): void {
  if (!images.length) return;
  idx = Math.max(0, Math.min(idx, images.length - 1));
  currentIdx = idx;
  const mainImg = document.getElementById('galleryMainImg') as HTMLImageElement | null;
  if (mainImg) {
    mainImg.style.opacity = '0';
    setTimeout(() => {
      mainImg.src = images[idx];
      mainImg.style.opacity = '1';
    }, 120);
  }
  const countBadge = document.getElementById('galleryCountBadge');
  if (countBadge) countBadge.textContent = (idx + 1) + ' / ' + images.length;
  document.querySelectorAll('.gallery-thumb').forEach((t, i) => {
    t.classList.toggle('active', i === idx);
  });
}

function buildGallery(imgs: string[], altText: string, fpBodyStyle: string | null): void {
  images = imgs || [];
  if (!images.length) {
    const galleryMain = document.getElementById('galleryMain');
    if (galleryMain) galleryMain.innerHTML = carPlaceholderHtml(fpBodyStyle);
    return;
  }

  const mainImg = document.getElementById('galleryMainImg') as HTMLImageElement | null;
  if (mainImg) {
    mainImg.src = images[0];
    mainImg.alt = altText;
  }
  const countBadge = document.getElementById('galleryCountBadge');
  if (countBadge) countBadge.textContent = '1 / ' + images.length;

  // nav buttons
  const prevBtn = document.getElementById('galleryPrev');
  const nextBtn = document.getElementById('galleryNext');
  if (images.length < 2) {
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
  } else {
    if (prevBtn) prevBtn.addEventListener('click', () => { showImage(currentIdx - 1); });
    if (nextBtn) nextBtn.addEventListener('click', () => { showImage(currentIdx + 1); });
  }

  // thumbnails
  const thumbContainer = document.getElementById('galleryThumbs');
  if (thumbContainer) {
    images.forEach((src, i) => {
      const thumb = document.createElement('div');
      thumb.className = 'gallery-thumb' + (i === 0 ? ' active' : '');
      thumb.innerHTML = '<img src="' + src + '" alt="Photo ' + (i + 1) + '" loading="lazy">';
      thumb.addEventListener('click', () => { showImage(i); });
      thumbContainer.appendChild(thumb);
    });
  }

  // keyboard nav
  document.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') showImage(currentIdx - 1);
    if (e.key === 'ArrowRight') showImage(currentIdx + 1);
  });
}

/* ── Spec table ── */
function buildSpecTable(v: StaticInventoryItem): string {
  const rows: Array<[string, string | number | null]> = [
    ['Year',            v.year],
    ['Make',            v.make],
    ['Model',           v.model],
    ['Trim',            v.trim],
    ['Body Style',      v.style],
    ['Condition',       conditionLabel(v.condition)],
    ['Mileage',         v.condition === 'N' || v.miles < 100 ? 'New' : v.miles.toLocaleString() + ' mi'],
    ['Drivetrain',      v.drivetrain],
    ['Fuel Type',       v.fuel || '—'],
    ['Exterior Color',  v.colorExtFancy || v.colorExt || '—'],
    ['Interior Color',  v.colorInt || '—'],
    ['Doors',           v.doors || '—'],
    ['Body Category',   (v.fpBodyStyle || '').replace(/\b\w/g, (c) => c.toUpperCase())],
    ['Stock #',         v.stock],
  ];
  return rows.map((r) => {
    return '<tr><td>' + r[0] + '</td><td>' + r[1] + '</td></tr>';
  }).join('');
}

/* ── Features ── */
function buildFeatures(features: Record<string, string[]> | null): string {
  if (!features || !Object.keys(features).length) {
    return '<p style="color:var(--secondary);font-size:0.88rem;">No feature data available.</p>';
  }
  return Object.keys(features).map((cat) => {
    const items = features[cat];
    if (!items || !items.length) return '';
    return '<div>'
      + '<div class="feature-category-name">' + cat + '</div>'
      + '<ul class="feature-list">'
      + items.map((f) => '<li>' + f + '</li>').join('')
      + '</ul>'
      + '</div>';
  }).join('');
}

/* ── Quick Specs panel ── */
function buildQuickSpecs(v: StaticInventoryItem): string {
  const milesDisplay = (v.condition === 'N' || v.miles < 100)
    ? 'New / 0 mi' : v.miles.toLocaleString() + ' mi';
  const rows: Array<[string, string | null]> = [
    ['Mileage',   milesDisplay],
    ['Drivetrain', v.drivetrain],
    ['Fuel',       v.fuel || '—'],
    ['Ext. Color', v.colorExtFancy || v.colorExt || '—'],
    ['Int. Color', v.colorInt || '—'],
    ['Body Style', (v.fpBodyStyle || '').replace(/\b\w/g, (c) => c.toUpperCase())],
  ];
  return rows.map((r) => {
    return '<div class="vdp-quick-spec-row">'
      + '<span class="spec-label">' + r[0] + '</span>'
      + '<span class="spec-val">' + r[1] + '</span>'
      + '</div>';
  }).join('');
}

/* ── Similar vehicles ── */
function buildSimilarVehicles(allVehicles: StaticInventoryItem[], current: StaticInventoryItem): void {
  let similar = allVehicles.filter((v) => {
    return v.stock !== current.stock && v.make === current.make;
  });
  if (similar.length < 3) {
    similar = allVehicles.filter((v) => {
      return v.stock !== current.stock && v.fpBodyStyle === current.fpBodyStyle;
    });
  }
  // pick up to 3
  similar = similar.slice(0, 3);
  if (!similar.length) return;

  const grid = document.getElementById('similarGrid');
  if (!grid) return;
  grid.innerHTML = similar.map(buildVehicleCard).join('');
  initPrequalTriggers(grid);

  const similarTitle = document.getElementById('similarTitle');
  if (similarTitle) similarTitle.textContent = 'More ' + current.make + ' Vehicles';
}

/* ── Main init ── */
loadInventory().then((data) => {
  const vehicles = data.inventory || [];
  const params   = new URLSearchParams(window.location.search);
  const stock    = params.get('stock') || '';

  const vehicle = vehicles.find((v) => v.stock === stock);

  const loadingEl = document.getElementById('vdpLoading');
  if (loadingEl) loadingEl.style.display = 'none';

  if (!vehicle) {
    const errorEl = document.getElementById('vdpError');
    if (errorEl) errorEl.style.display = 'block';
    return;
  }

  /* Update page meta */
  const fullName = vehicle.year + ' ' + vehicle.make + ' ' + vehicle.model + ' ' + vehicle.trim;
  document.title = fullName + ' | FlexPath DXP Demo';
  const breadcrumb = document.getElementById('vdpBreadcrumb');
  if (breadcrumb) breadcrumb.textContent = vehicle.year + ' ' + vehicle.make + ' ' + vehicle.model;

  /* Condition badge */
  const badge = document.getElementById('vdpCondBadge');
  if (badge) {
    badge.textContent = conditionLabel(vehicle.condition);
    if (vehicle.condition === 'C') badge.style.background = '#b4b4b3';
  }

  /* Title / subtitle / stock */
  const titleEl = document.getElementById('vdpTitle');
  if (titleEl) titleEl.textContent = vehicle.year + ' ' + vehicle.make + ' ' + vehicle.model;
  const subtitleEl = document.getElementById('vdpSubtitle');
  if (subtitleEl) subtitleEl.textContent = vehicle.trim + ' · ' + vehicle.style;
  const stockEl = document.getElementById('vdpStock');
  if (stockEl) stockEl.textContent = 'Stock #' + vehicle.stock;

  /* Pricing */
  const priceEl = document.getElementById('vdpPrice');
  if (priceEl) priceEl.textContent = formatPrice(vehicle.priceMap ?? 0);
  const fpdxpEl = document.getElementById('vdpFpdxp');
  if (fpdxpEl) fpdxpEl.classList.add('fpdxp-vin-' + vehicle.vin, 'fpdxp-stock-' + vehicle.stock);

  // load the integration only after the real VIN/stock placeholders are in place
  loadIntegrationScript();

  /* Quick specs */
  const quickSpecsEl = document.getElementById('vdpQuickSpecs');
  if (quickSpecsEl) quickSpecsEl.innerHTML = buildQuickSpecs(vehicle);

  /* Spec table */
  const specTableEl = document.getElementById('vdpSpecTable');
  if (specTableEl) specTableEl.innerHTML = buildSpecTable(vehicle);

  /* Features */
  const featuresEl = document.getElementById('vdpFeatures');
  if (featuresEl) featuresEl.innerHTML = buildFeatures(vehicle.features);

  /* Gallery */
  buildGallery(vehicle.images || [], fullName, vehicle.fpBodyStyle);

  /* Similar vehicles */
  buildSimilarVehicles(vehicles, vehicle);

  /* Show content */
  const contentEl = document.getElementById('vdpContent');
  if (contentEl) contentEl.style.display = 'block';

  /* Wire up newly rendered prequal triggers */
  const contentForTriggers = document.getElementById('vdpContent');
  if (contentForTriggers) initPrequalTriggers(contentForTriggers);

}).catch((err) => {
  console.error('VDP load error:', err);
  const loadingEl = document.getElementById('vdpLoading');
  if (loadingEl) loadingEl.style.display = 'none';
  const errorEl = document.getElementById('vdpError');
  if (errorEl) errorEl.style.display = 'block';
});
